import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, X, Minimize2, Maximize2, Info, Send } from 'lucide-react';
import '../../assets/Styles/ContextTransferPanelCSS/ContextPanel.css';
import APICom from "../../utils/helpers/APICom.js";
import moment from "moment";
// --- Custom Hook for Element Height Measurement ---
const useElementHeight = (ref) => {
const [height, setHeight] = useState(0);
useEffect(() => {
const handleResize = () => {
if (ref.current) {
setHeight(ref.current.offsetHeight);
}
};
handleResize(); // Set initial height
window.addEventListener('resize', handleResize);
return () => window.removeEventListener('resize', handleResize);
}, [ref]);
return height;
};
// ----------------------------------------------------
const ContextTransferPanel = (props) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandedDetailItems, setExpandedDetailItems] = useState(new Set());
  const [contextData, setContextData] = useState([]);
  const [loading, setLoading] = useState(false);
  const xCancelSourceForGrid = useRef(null);
  // Refs for height calculation
  const contentPanelRef = useRef(null); // Ref for the main scrollable content area
  const itemRefs = useRef([]); // Ref for the currently expanded accordion item container
  // State to hold the dynamically calculated max height for the chat detail
  const [detailMaxHeight, setDetailMaxHeight] = useState('300px');
  // Helper to add/remove refs dynamically
  if (contextData.length !== itemRefs.current.length) {
  itemRefs.current = Array(contextData.length).fill().map((_, i) => itemRefs.current[i] || React.createRef());
  }
  // --- Dynamic Height Calculation Logic ---
  useEffect(() => {
  const calculateMaxHeight = () => {
  if (!contentPanelRef.current || contextData.length === 0) return;
  // 1. Get the total height available in the context-panel-content area
  const totalContentHeight = contentPanelRef.current.offsetHeight;
  // 2. Sum the heights of all other accordion items that are *not* currently the expanded one
  // We are looking for the accordion item whose details are currently open.
  const expandedIndex = Array.from(expandedDetailItems).length === 1 ? Array.from(expandedDetailItems)[0] : -1;
  if (expandedIndex === -1) {
  // No detail is open, no dynamic calculation needed. Use default or leave auto.
  setDetailMaxHeight('auto');
  return;
  }
  // 3. Get the expanded item container (context-accordion-item)
  const expandedItemRef = itemRefs.current[expandedIndex];
  if (!expandedItemRef || !expandedItemRef.current) return;
  // Height of the header + padding/margin/borders within the expanded item (summary, buttons, etc.)
  // Instead of calculating each element, we get the height of the full content and subtract
  // the height of the elements *outside* the accordion item and the height of the summary.
  // A simpler way is to:
  // a. Get the position/height of the item *relative to the content panel*.
  const itemContainerRect = expandedItemRef.current.getBoundingClientRect();
  const contentPanelRect = contentPanelRef.current.getBoundingClientRect();
  // Total vertical space *before* the current item + its height (approx. to content panel bottom)
  const itemTopRelativeToContent = itemContainerRect.top - contentPanelRect.top;
  // Height of all elements (header, other items, gaps) *above* the current expanded item.
  // Note: contentPanelRef contains padding: 16px top and bottom.
  const contentPanelPadding = 16;
  // Calculate height of everything *above* the detail section button.
  // We use the DOM elements' positions and height within the expanded item to calculate
  // the remaining space.
  const summaryButton = expandedItemRef.current.querySelector('.context-accordion-button');
  const showDetailsButton = expandedItemRef.current.querySelector('.show-details-button');
  const loadContextButton = expandedItemRef.current.querySelector('.load-context-button');
  if (!summaryButton || !showDetailsButton || !loadContextButton) return;
  const summaryHeight = summaryButton.offsetHeight;
  const detailsButtonHeight = showDetailsButton.offsetHeight;
  const loadButtonHeight = loadContextButton.offsetHeight;
  const loadButtonMargin = 10; // marginTop: '10px' on load context button.
  const itemMarginBottom = 12; // margin-bottom: 12px on context-accordion-item.
  const detailSectionButtonMargin = 8; // marginBottom: '8px' on show-details-button.
  const totalFixedHeightsInItem = summaryHeight + detailsButtonHeight + loadButtonHeight + loadButtonMargin + detailSectionButtonMargin;
  // Total height of the content panel from top to bottom
  const totalContentPanelHeight = contentPanelRef.current.offsetHeight;
  // Sum of all other context accordion items' heights (non-expanded)
  let otherItemsHeight = 0;
  itemRefs.current.forEach((ref, index) => {
  if (index !== expandedIndex && ref.current) {
  otherItemsHeight += ref.current.offsetHeight + itemMarginBottom;
  }
  });
  // Height taken by the expanded item's fixed parts: title, summary (if open), buttons.
  const totalSpaceTakenByFixedContent = (
  (itemRefs.current.length) * itemMarginBottom // Sum of all margins (even the last one)
  + contentPanelPadding * 2 // Top and bottom padding of context-panel-content
  + otherItemsHeight // All other items' heights
  + totalFixedHeightsInItem // Fixed height of current item
  );
  // Remaining space is for the current detail section
  const calculatedMaxHeight = totalContentPanelHeight - totalSpaceTakenByFixedContent;
  // Set the new max height, subtract a buffer for better visual look/safety
  // We use a buffer of 16px to ensure it doesn't touch the bottom edge.
  setDetailMaxHeight(`${Math.max(150, calculatedMaxHeight - 16)}px`);
  };
  // Recalculate max height whenever detail section is expanded/collapsed or window resizes
  calculateMaxHeight();
  const resizeObserver = new ResizeObserver(calculateMaxHeight);
  if (contentPanelRef.current) {
  resizeObserver.observe(contentPanelRef.current);
  }
  return () => {
  if (contentPanelRef.current) {
  resizeObserver.unobserve(contentPanelRef.current);
  }
  };
  }, [expandedDetailItems, contextData.length]);
  // Fetch context data from API
  const fetchContextData = async () => {
    setLoading(true);
    try {
      if(props.ctpaneldata.length > 0)
      {
        const parsedData = props.ctpaneldata;
        const contextItems = [];
       
         parsedData.forEach((item, index) => {
          const tripInfo = item.addressInfo.trips[0].details[0].tripInfo;
          const contextPhoneNo = item.riderInfo.phoneNo;
          let bClearContext = (props.ctatspid !== item.generalInfo.requestAffiliateID.toString()) ? true: false;
          if(bClearContext)
          {
            tripInfo.contextCallTitle = "";
            tripInfo.contextCallSummary = "";
            tripInfo.contextCallDetail = {};
          }  
          contextItems.push({
            ID: index + 1,
            ContextCallTitle: ((tripInfo.contextCallTitle !== "") ? tripInfo.contextCallTitle : "No Context Recorded"),
            ContextCallSummary: ((tripInfo.contextCallSummary!== "") ? tripInfo.contextCallSummary : "No context available for phone# : " + contextPhoneNo),
            ContextCallDetail: tripInfo.contextCallDetail,
            ContextRecordDate: tripInfo.contextCallRecordDateTime,
            IVRData: item
          });
        });
        setExpandedItems(new Set(contextItems.map((_, i) => i)));
        setContextData(contextItems);
      }
    } catch (error) {
      console.error('Error fetching context data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContextData();
  }, []);

  const fetchLatestContextData = async () => {
    if(props.ctphoneno)
    {
      const ContextCallResponse = await APICom.fetchData("ReservationCoreURL",`api/CallTransferService/GetLatestContextTransfer/${props.ctphoneno}`, "get", 'accept: */*', null, xCancelSourceForGrid.current);
      if (ContextCallResponse.data.success) {
        const parsedData = JSON.parse(ContextCallResponse.data.data);
        const ContextDateTime = moment(ContextCallResponse.data.contextRecordedDate, "M/D/YYYY h:mm A").format("M/D/YYYY h:mm A");
        
      }
    }
  }

  const toggleSummaryExpand = (index) => {
  const newExpanded = new Set(expandedItems);
  if (newExpanded.has(index)) newExpanded.delete(index);
  else newExpanded.add(index);
  setExpandedItems(newExpanded);
  };

  const toggleDetailExpand = (index) => {
    const newExpandedDetail = new Set(expandedDetailItems);
    // Collapse all others and expand only the clicked one.
    if (newExpandedDetail.has(index)) {
      newExpandedDetail.clear();
    } else {
      newExpandedDetail.clear();
      newExpandedDetail.add(index);
    }
    setExpandedDetailItems(newExpandedDetail);
  };

   const handleItemAction = (id) => {
    if(contextData && contextData.length > 0)
    {
      try
      {
        contextData.forEach((item, index) => {
            if(item.ID === id && item.ContextCallTitle !== "No Context Recorded")
            {
                props.ctpopulatedata(item.IVRData, "", true);
            }
        });
      }catch(asdasd)
      {
        console.log("Load Context Data Failed");
      }
    }
    

  };

function renderConversation(conversationData) {
  try {
    // Parse JSON if it's a string
    const parsed =
      typeof conversationData === "string"
        ? JSON.parse(conversationData)
        : conversationData;

    if (!parsed || !Array.isArray(parsed.conversation)) {
      return `<div class="no-conversation">No conversation found</div>`;
    }

    let html = `<div class="chat-container">`;

    parsed.conversation.forEach((msg) => {
      const roleClass = msg.role === "agent" ? "agent-bubble" : "user-bubble";
      const sender = msg.role === "agent" ? "Agent" : "User";

      html += `
        <div class="chat-bubble ${roleClass}">
          <div class="sender">${sender}</div>
          <div class="message">${escapeHtml(msg.content)}</div>
          <div class="timestamp">${formatTimestamp(msg.timestamp)}</div>
        </div>
      `;
    });

    html += `</div>`;
    return html;
  } catch (err) {
    console.error("Error parsing conversation JSON:", err);
    return `<div class="no-conversation">No conversation found</div>`;
  }
}

function escapeHtml(text = "") {
  return text
    .toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  return `<span>${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second:"2-digit"
  })}</span>`;
}

  // Don't render if no data
  if (!contextData || contextData.length === 0) {
    return null;
  }

  
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="context-floating-button"
        title="Show Context Information"
        style={{
          position: 'fixed',
          right: '0',
          top: '83vh',
          transform: 'translateY(-50%)',
          width: '48px',
          height: '48px',
          //borderRadius: '8px 0 0 8px',
          backgroundColor: '#07447b',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1056
        }}
      >
        <Info style={{ width: '20px', height: '20px' }} />
      </button>
    );
  }

  return (
    <div 
      className={`context-panel ${isMinimized ? 'minimized' : 'expanded'}`}
      style={{
        position: 'fixed',
        right: '0px',
        bottom: '20px',
        width: isMinimized ? '60px' : '400px',
        maxHeight: '83vh',
        backgroundColor: 'white',
        //borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        zIndex: 1056,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <div 
        className="context-panel-header"
        style={{
          backgroundColor: '#07447b',
          color: 'white',
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {!isMinimized && (
          <div 
            className="context-panel-header-title"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Info style={{ width: '20px', height: '20px' }} />
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Context Information</h4>
          </div>
        )}
        <div 
          className="context-panel-header-buttons"
          style={{
            display: 'flex',
            gap: '8px'
          }}
        >
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="context-icon-button"
            title={isMinimized ? "Expand" : "Minimize"}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isMinimized ? (
              <Maximize2 style={{ width: '16px', height: '16px' }} />
            ) : (
              <Minimize2 style={{ width: '16px', height: '16px' }} />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="context-icon-button"
            title="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div 
          className="context-panel-content"
          ref={contentPanelRef} // Attach ref to content panel
          style={{
            flex: 1,
            padding: '16px',
            overflowY: 'auto' // Important for content to scroll
          }}
        >
          {loading ? (
            <div 
              className="context-loader"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px'
              }}
            >
              <div 
                className="context-spinner"
                style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f4f6',
                  borderTop: '4px solid #07447b',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              ></div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {contextData.map((item, index) => (
                <div 
                  key={item.ID}
                  className="context-accordion-item"
                  ref={itemRefs.current[index]} // Attach ref to accordion item
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                >
                  <button
                    onClick={() => toggleSummaryExpand(index)}
                    className="context-accordion-button"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div>
                      {item.ContextCallTitle && (
                        <h4
                          className="context-accordion-title"
                          style={{
                            margin: '0 0 8px 0',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#1f2937'
                          }}
                        >
                          {expandedItems.has(index) ? (
                            <ChevronDown style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                          ) : (
                            <ChevronRight style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                          )}
                          <span>{item.ContextCallTitle} [{item.ContextCallTitle !== "No Context Recorded" ? `(${item.ContextRecordDate})` : ''}]</span>
                        </h4>
                      )}

                      {/* ---- render summary only when the summary accordion is expanded ---- */}
                      {expandedItems.has(index) && item.ContextCallSummary && (
                        <div
                          className="context-accordion-summary"
                          style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            marginLeft: '24px',
                            marginBottom: '8px'
                          }}
                          dangerouslySetInnerHTML={{ __html: item.ContextCallSummary }}
                        />
                      )}
                    </div>
                  </button>

                  {item.ContextCallDetail && (
                  <div style={{ marginLeft: '24px', marginBottom: '12px' }}>
                    <button
                      onClick={() => toggleDetailExpand(index)}
                      className="show-details-button" // Added class for ref querying
                      style={{
                        background: 'transparent',
                        color: '#07447b',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '8px'
                      }}
                    >
                      {expandedDetailItems.has(index) ? (
                        <>
                          <ChevronDown style={{ width: '14px', height: '14px' }} />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronRight style={{ width: '14px', height: '14px' }} />
                          Show Details
                        </>
                      )}
                    </button>

                    {expandedDetailItems.has(index) && (
                      <div
                        className="context-accordion-detail"
                        style={{
                          fontSize: '13px',
                          color: '#4b5563',
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          maxHeight: detailMaxHeight, // Use dynamic max height
                          overflowY: 'auto',
                          transition: 'max-height 0.3s ease-in-out' // Smooth transition 
                        }}
                        dangerouslySetInnerHTML={{
                          __html: renderConversation(item.ContextCallDetail)
                        }}
                      />
                    )}

                    <button
                      onClick={() => handleItemAction(item.ID)}
                      className="load-context-button" // Added class for ref querying
                      style={{
                        marginTop: '10px',
                        padding: '8px 16px',
                        backgroundColor: '#07447b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="Load IVR Data"
                    >
                      <Send style={{ width: '14px', height: '14px' }} />
                      Load Context Data
                    </button>
                  </div>
                )}
                </div>
              ))}

              <button
                onClick={fetchLatestContextData}
                disabled={loading}
                className="context-refresh-button"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#07447b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginTop: '8px',
                  opacity: loading ? 0.6 : 1,
                  display: 'none'
                }}
              >
                {loading ? 'Refreshing...' : 'Refresh Context'}
              </button>
            </div>
          )}
        </div>
      )}

      {isMinimized && (
        <div 
          className="context-minimized-content"
          // ... (minimized content logic remains the same) ...
          style={{
            padding: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div 
            className="context-minimized-text"
            style={{
              fontSize: '10px',
              fontWeight: '600',
              color: '#07447b',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }}
          >
            CONTEXT TRANFER PANEL MINIMIZED
          </div>
        </div>
      )}
    </div>
  );
};


export default ContextTransferPanel;